/**
 * Delivery Calendar Picker v2 - Web Component
 *
 * Auto-initializes when added to DOM, just like other Shopify components.
 * No manual initialization needed!
 */

const SHIP_DATE_EVENT_NAME = 'delivery-calendar:ship-date-change';
const SHIP_DATE_STATE_KEY = '__deliveryCalendarState';

const getSharedShipDateState = () => {
  if (typeof window === 'undefined') return null;
  return window[SHIP_DATE_STATE_KEY] || null;
};

const setSharedShipDateState = (state) => {
  if (typeof window === 'undefined') return state;
  window[SHIP_DATE_STATE_KEY] = state;
  return state;
};

const SHIP_DATE_RESET_KEY = 'snowcubes:ship-date-reset';
const SHIP_DATE_SAVE_KEY = 'snowcubes:ship-date-save';
const SHIP_DATE_REQUEST_MS = 5000;
const SHIP_DATE_RECOVERY_MS = 30000;
const SHIP_DATE_SAVE_ATTEMPTS = 3;
// Each attempt reads identity and posts; the successful attempt also reads back.
const SHIP_DATE_SAVE_BUDGET_MS = (2 * SHIP_DATE_SAVE_ATTEMPTS + 1) * SHIP_DATE_REQUEST_MS
  + 1000 * (2 ** (SHIP_DATE_SAVE_ATTEMPTS - 1) - 1);

const getShipDateReset = () => {
  if (window.__snowcubesShipDateReset) return window.__snowcubesShipDateReset;
  try {
    const emptyCart = Boolean(window.sessionStorage.getItem(SHIP_DATE_RESET_KEY));
    const saved = JSON.parse(window.sessionStorage.getItem(emptyCart ? SHIP_DATE_RESET_KEY : SHIP_DATE_SAVE_KEY));
    if (typeof saved?.token === 'string' && saved.token && (emptyCart || typeof saved.value === 'string' && saved.value)) {
      window.__snowcubesShipDateReset = {
        token: saved.token, pending: true, emptyCart, value: saved.value || '',
        ended: emptyCart && !saved.value, failed: true, indeterminate: Boolean(saved.indeterminate),
        // A new realm has no completion handle for the old remote write.
        orphaned: Boolean(saved.indeterminate),
        holdUntil: Number(saved.holdUntil) || Date.now() + SHIP_DATE_RECOVERY_MS,
      };
    }
  } catch (_error) { /* Storage may be unavailable. Keep the in-page guard. */ }
  return window.__snowcubesShipDateReset || null;
};

const persistShipDateReset = (reset) => {
  window.__snowcubesShipDateReset = reset;
  try {
    if ((reset.pending || hasShipDateRequests(reset.token) || reset.flight) && reset.token) {
      // Only cart emptying creates the reset marker. A pick retains its intent
      // separately so a new page can recover it without ever issuing a clear.
      window.sessionStorage.setItem(reset.emptyCart ? SHIP_DATE_RESET_KEY : SHIP_DATE_SAVE_KEY, JSON.stringify({
        token: reset.token, indeterminate: Boolean(reset.indeterminate || hasShipDateRequests(reset.token) || reset.flight),
        holdUntil: reset.holdUntil, value: reset.value || '',
      }));
      window.sessionStorage.removeItem(reset.emptyCart ? SHIP_DATE_SAVE_KEY : SHIP_DATE_RESET_KEY);
    } else {
      window.sessionStorage.removeItem(SHIP_DATE_RESET_KEY);
      window.sessionStorage.removeItem(SHIP_DATE_SAVE_KEY);
    }
  } catch (_error) { /* Storage denial must not admit checkout. */ }
};

const showShipDateSaveError = (failed) => {
  const message = window.cartStrings?.error || window.SnowcubesLocale?.['sections.cart.cart_error'] || '';
  document.querySelectorAll('.Cart-Errors').forEach((node) => {
    if (failed) {
      if (!node.textContent || node.dataset.shipDateResetError === node.textContent) {
        node.textContent = message;
        node.dataset.shipDateResetError = message;
      }
    } else if (node.dataset.shipDateResetError === node.textContent) {
      node.textContent = '';
      delete node.dataset.shipDateResetError;
    }
  });
};

// The deadline settles locally even when fetch ignores abort. A late HTTP
// response is useful for ordering; a timeout or transport rejection is not
// proof that Shopify canceled the mutation.
const shipDateRequest = async (url, init = {}, readBody = false, onLate) => {
  const controller = new AbortController();
  let expired = false;
  let timer;
  const request = Promise.resolve().then(async () => {
    const response = await fetch(url, { ...init, signal: controller.signal });
    if (!response.ok) return { outcome: 'failed' };
    return { outcome: 'saved', cart: readBody ? await response.json() : null };
  });
  request.then((result) => { if (expired) onLate?.(result); }, () => {});
  try {
    return await Promise.race([
      request,
      new Promise((resolve) => {
        timer = setTimeout(() => {
          expired = true;
          resolve({ outcome: 'indeterminate' });
          controller.abort();
        }, SHIP_DATE_REQUEST_MS);
      }),
    ]);
  } catch (_error) {
    return { outcome: 'indeterminate' };
  } finally {
    clearTimeout(timer);
  }
};

const shipDateCartUrl = () => `${String(window.Shopify?.routes?.root || '/').replace(/\/+$/, '')}/cart.js`;
const readShipDateCart = async (attempts = 1) => {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const result = await shipDateRequest(shipDateCartUrl(), { cache: 'no-store' }, true);
    const cart = result.cart;
    if (result.outcome === 'saved' && (!cart?.token || !cart.attributes || typeof cart.attributes !== 'object' || Array.isArray(cart.attributes))) {
      result.outcome = 'failed';
    }
    if (result.outcome === 'saved' || attempt === attempts) return result;
    await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
  }
};

// One logical writer and the newest waiting pick. A lost response holds checkout
// for one grace period, then the latest intent is resent and read back.
const shipDateWriter = {
  next: null, running: false, wake: null, orderEnded: false, clearPending: false,
  revision: 0, confirmedRevision: 0, latest: null, requests: new Set(),
};

const hasShipDateRequests = (token = getShipDateReset()?.token) => (
  [...shipDateWriter.requests].some((task) => !token || task.token === token)
);

const useShipDateCart = (cart) => {
  const current = getShipDateReset();
  if (current?.token === cart.token) return current;
  const replacedIntent = shipDateWriter.latest?.token && shipDateWriter.latest.token !== cart.token;
  if (replacedIntent) {
    shipDateWriter.latest = null;
    shipDateWriter.next?.resolve('superseded');
    shipDateWriter.next = null;
    setSharedShipDateState(null);
  }
  const reset = { token: cart.token, pending: false, ended: false, verified: true };
  persistShipDateReset(reset);
  shipDateWriter.clearPending = false;
  shipDateWriter.orderEnded = false;
  showShipDateSaveError(false);
  if (replacedIntent) {
    const detail = { value: cart.attributes.ship_date || '', status: 'synced', token: cart.token };
    setSharedShipDateState(detail);
    window.dispatchEvent(new CustomEvent(SHIP_DATE_EVENT_NAME, { detail }));
  }
  return reset;
};

const completeShipDateReset = (reset) => {
  reset.pending = false;
  reset.failed = false;
  reset.indeterminate = false;
  reset.orphaned = false;
  reset.emptyCart = false;
  reset.recoveryDeferred = false;
  clearTimeout(reset.recoveryTimer);
  reset.recoveryTimer = null;
  reset.holdUntil = 0;
  persistShipDateReset(reset);
  shipDateWriter.clearPending = false;
  showShipDateSaveError(false);
};

const publishConfirmedShipDate = (value, revision) => {
  const shared = getSharedShipDateState();
  if (!value || revision !== shipDateWriter.revision || shared?.value !== value || shared.status === 'synced') return;
  const payload = { ...shared, status: 'synced' };
  setSharedShipDateState(payload);
  window.dispatchEvent(new CustomEvent(SHIP_DATE_EVENT_NAME, { detail: payload }));
};

const reassertLatestShipDate = (token) => {
  const intent = shipDateWriter.latest;
  if (!intent || (intent.token && intent.token !== token)) return;
  shipDateWriter.next = intent;
  if (shipDateWriter.running) shipDateWriter.wake?.();
  else drainShipDateWrites();
};

const scheduleShipDateRecovery = (reset) => {
  // Also takes over a fallback timer when the calendar loads during its hold.
  reset.recover = () => {
    if (getShipDateReset() !== reset) return;
    clearTimeout(reset.recoveryTimer);
    reset.recoveryTimer = null;
    // The save has its own bounded request deadlines. Do not retire its live
    // requests or queue a duplicate while it is still posting or reading back.
    if (shipDateWriter.running) {
      reset.recoveryDeferred = true;
      reset.holdUntil = Date.now() + SHIP_DATE_REQUEST_MS;
      scheduleShipDateRecovery(reset);
      return;
    }
    reset.holdUntil = 0;
    reset.orphaned = false;
    reset.flight = null;
    reset.indeterminate = false;
    for (const task of shipDateWriter.requests) {
      if (task.token === reset.token) shipDateWriter.requests.delete(task);
    }
    persistShipDateReset(reset);
    if (shipDateWriter.latest) reassertLatestShipDate(reset.token);
    else if (reset.emptyCart) writeShipDate('', reset.token);
  };
  if (reset.recoveryTimer) return;
  reset.holdUntil ||= Date.now() + SHIP_DATE_RECOVERY_MS;
  reset.recoveryTimer = setTimeout(() => reset.recover(), Math.max(0, reset.holdUntil - Date.now()));
  persistShipDateReset(reset);
};

const observeFallbackShipDateRequest = (reset) => {
  const flight = reset.flight;
  if (!flight || reset.observedFlight === flight) return;
  reset.observedFlight = flight;
  scheduleShipDateRecovery(reset);
  flight.then(() => {
    if (getShipDateReset() !== reset || reset.flight !== flight) return;
    reset.flight = null;
    reset.indeterminate = false;
    persistShipDateReset(reset);
    reassertLatestShipDate(reset.token);
  }, () => { /* The grace deadline recovers a rejected transport too. */ });
};

const postShipDate = async (intent) => {
  let reset = getShipDateReset();
  if (reset?.cleanup) await reset.cleanup;
  reset ||= { token: intent.token || '', pending: false, ended: false };
  reset.pending = true;
  reset.value = intent.value;
  scheduleShipDateRecovery(reset);
  const current = () => shipDateWriter.latest === intent;
  for (let attempt = 1; attempt <= SHIP_DATE_SAVE_ATTEMPTS; attempt += 1) {
    const identity = await readShipDateCart();
    if (!current()) return 'superseded';
    if (identity.outcome !== 'saved') return identity.outcome;
    intent.token ||= identity.cart.token;
    reset = useShipDateCart(identity.cart);
    if (identity.cart.token !== intent.token || !current()) return 'superseded';
    reset.verified = true;
    if (reset.pending) scheduleShipDateRecovery(reset);
    if (reset.flight) observeFallbackShipDateRequest(reset);
    if (hasShipDateRequests(intent.token) || (reset.pending && (reset.orphaned || reset.flight))) return 'indeterminate';
    const formData = new FormData();
    formData.append('attributes[ship_date]', intent.value);
    const task = { token: intent.token, reset };
    shipDateWriter.requests.add(task);
    reset.pending = true;
    reset.value = intent.value;
    scheduleShipDateRecovery(reset);
    const root = window.Shopify?.routes?.root || '/';
    const result = await shipDateRequest(`${String(root).replace(/\/+$/, '')}/cart/update.js`, {
      method: 'POST', body: formData,
    }, false, () => {
      shipDateWriter.requests.delete(task);
      if (getShipDateReset() === task.reset && !task.reset.orphaned && !hasShipDateRequests(task.token)) {
        task.reset.indeterminate = false;
        persistShipDateReset(task.reset);
      }
      reassertLatestShipDate(task.token);
    });
    if (result.outcome === 'indeterminate') {
      if (getShipDateReset() === task.reset) {
        task.reset.indeterminate = true;
        persistShipDateReset(task.reset);
      }
      return 'indeterminate';
    }
    shipDateWriter.requests.delete(task);
    if (getShipDateReset() === task.reset) persistShipDateReset(task.reset);
    if (result.outcome === 'saved') {
      if (!current()) return 'superseded';
      if (task.reset.pending) {
        const readback = await readShipDateCart();
        if (!current()) return 'superseded';
        if (readback.outcome !== 'saved') return readback.outcome;
        if (readback.cart.token !== intent.token) {
          useShipDateCart(readback.cart);
          return 'superseded';
        }
        if ((readback.cart.attributes.ship_date || '') !== intent.value) return 'failed';
        if (getShipDateReset() === task.reset) completeShipDateReset(task.reset);
      }
      return 'saved';
    }
    if (!current()) return 'superseded';
    if (attempt === SHIP_DATE_SAVE_ATTEMPTS) return 'failed';
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, 1000 * Math.pow(2, attempt - 1));
      shipDateWriter.wake = () => { clearTimeout(timer); resolve(); };
    });
    shipDateWriter.wake = null;
    if (!current()) return 'superseded';
  }
};

const drainShipDateWrites = async () => {
  shipDateWriter.running = true;
  try {
    while (shipDateWriter.next) {
      const intent = shipDateWriter.next;
      const { value, resolve, revision } = intent;
      shipDateWriter.next = null;
      const outcome = await postShipDate(intent);
      if (outcome === 'failed' || outcome === 'indeterminate') {
        const reset = getShipDateReset();
        if (reset?.pending) reset.failed = true;
        showShipDateSaveError(true);
        document.querySelectorAll('delivery-calendar-picker').forEach((picker) => {
          if (picker.pendingCheckout?.revision === revision) picker.pendingCheckout = null;
        });
      } else if (outcome === 'saved') {
        shipDateWriter.confirmedRevision = revision;
        showShipDateSaveError(false);
        publishConfirmedShipDate(value, revision);
      }
      resolve(shipDateWriter.next ? 'superseded' : outcome);
    }
  } finally {
    shipDateWriter.running = false;
    const reset = getShipDateReset();
    if (reset?.recoveryDeferred) {
      reset.recoveryDeferred = false;
      reset.recover();
    }
    document.querySelectorAll('delivery-calendar-picker').forEach((picker) => picker.replayCheckout?.());
  }
};

const writeShipDate = (value, token = null) => new Promise((resolve) => {
  shipDateWriter.next?.resolve('superseded');
  shipDateWriter.revision += 1;
  const intent = { value, token, resolve, revision: shipDateWriter.revision };
  shipDateWriter.latest = intent;
  shipDateWriter.next = intent;
  if (value) {
    shipDateWriter.orderEnded = false;
    const reset = getShipDateReset();
    if (reset) {
      reset.ended = false;
      reset.value = value;
      persistShipDateReset(reset);
    }
  }
  if (shipDateWriter.running) shipDateWriter.wake?.();
  else drainShipDateWrites();
});

if (typeof document !== 'undefined') {
  document.addEventListener('cart:emptied', (event) => {
    event.preventDefault();
    setSharedShipDateState(null);
    shipDateWriter.orderEnded = true;
    const previous = getShipDateReset();
    if (!shipDateWriter.running && !hasShipDateRequests(event.detail?.token) && !event.detail?.shipDate && !previous?.pending) return;
    const token = event.detail?.token || '';
    const reset = previous;
    if (!reset?.emptyCart || reset.token !== token) return;
    reset.ended = true;
    reset.verified = true;
    reset.indeterminate ||= Boolean(hasShipDateRequests(token) && !shipDateWriter.running);
    persistShipDateReset(reset);
    shipDateWriter.clearPending = true;
    writeShipDate('', token);
  });
}

class DeliveryCalendarPicker extends HTMLElement {
  // Timing constants - tuned for smooth 60fps feel
  static SCROLL_ANIMATION_MS = 280;    // Native scroll-behavior: smooth duration
  static SCROLL_SETTLE_MS = 50;        // Buffer after scroll before re-centering
  static SCROLL_DEBOUNCE_MS = 150;     // Fallback debounce if no scrollend event
  static NAV_LOCK_TIMEOUT_MS = 1000;   // Emergency unlock if isNavigating gets stuck
  // The Liquid host supplies all customer-facing copy through data-* attrs.
  // Keep this compatibility constant empty so a missing payload never leaks a
  // second, English-only source of truth.
  static DEFAULT_AWAY_MESSAGE = '';

  constructor() {
    super();
    this.selectionGeneration = 0;
    this.calendarInstanceId = `delivery-calendar-${Math.random().toString(36).substr(2, 9)}`;

    // Configuration constants
    this.DEFAULT_DELIVERY_WEEKDAY = 1; // Monday
    this.DEFAULT_LEAD_TIME_DAYS = 3;
    // Active lead time (days an order must be placed before its ship date).
    // Drives BOTH which dates are offered (generateDeliveryDates) AND when a
    // date locks (isPastCutoff). initCalendar overwrites this from the
    // data-lead-time-days metafield; the default keeps isPastCutoff correct if
    // a method is called before init.
    this.leadTimeDays = this.DEFAULT_LEAD_TIME_DAYS;
    this.DEFAULT_DATE_RANGE_WEEKS = 8; // 8 weeks forward (56 days)
    this.cutoffHour = 15;
    this.cutoffMinute = 0;

    this.locale = 'en-US';
    this.monthNames = this.buildDateNameList(this.locale, 'month', 'long');
    this.monthNamesShort = this.buildDateNameList(this.locale, 'month', 'short');
    this.weekdayNames = this.buildDateNameList(this.locale, 'weekday', 'short');
    this.localeCopy = {};
    this.isTestEnv = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
    this.isDebugEnabled = typeof window !== 'undefined' && Boolean(window.DELIVERY_CALENDAR_DEBUG);

    // Refs for cleanup
    this._scrollTimeout = null;
    this._navTimeout = null;
    this._settleTimeout = null; // Separate ref for settle timeout (avoids overwriting _navTimeout)
    this._visibilityObserver = null;
  }

  resolveLocale() {
    const documentLocale = typeof document !== 'undefined' ? document.documentElement?.lang : '';
    return String(this.dataset?.locale || documentLocale || 'en-US').replace('_', '-');
  }

  buildDateNameList(locale, unit, style) {
    const values = [];
    try {
      const formatter = new Intl.DateTimeFormat(locale, { [unit]: style });
      const start = unit === 'weekday' ? new Date(2021, 7, 1, 12, 0, 0) : new Date(2021, 0, 1, 12, 0, 0);
      for (let index = 0; index < 12; index += 1) {
        const date = unit === 'weekday'
          ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + index, 12, 0, 0)
          : new Date(start.getFullYear(), start.getMonth() + index, 1, 12, 0, 0);
        values.push(formatter.format(date));
      }
      return unit === 'weekday' ? values.slice(0, 7) : values;
    } catch (_error) {
      const formatter = new Intl.DateTimeFormat('en-US', { [unit]: style });
      const start = unit === 'weekday' ? new Date(2021, 7, 1, 12, 0, 0) : new Date(2021, 0, 1, 12, 0, 0);
      const count = unit === 'weekday' ? 7 : 12;
      return Array.from({ length: count }, (_, index) => formatter.format(
        unit === 'weekday'
          ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + index, 12, 0, 0)
          : new Date(start.getFullYear(), start.getMonth() + index, 1, 12, 0, 0),
      ));
    }
  }

  setLocaleData() {
    this.locale = this.resolveLocale();
    this.monthNames = this.buildDateNameList(this.locale, 'month', 'long');
    this.monthNamesShort = this.buildDateNameList(this.locale, 'month', 'short');
    this.weekdayNames = this.buildDateNameList(this.locale, 'weekday', 'short');
    this.localeCopy = {
      heading: this.dataset.heading || '',
      summaryDefault: this.dataset.summaryDefault || '',
      required: this.dataset.requiredMessage || '',
      prevMonth: this.dataset.prevMonth || '',
      nextMonth: this.dataset.nextMonth || '',
      jumpToSelected: this.dataset.jumpToSelected || '',
      unavailable: this.dataset.unavailable || '',
      nextAvailable: this.dataset.nextAvailable || '',
      ships: this.dataset.ships || '',
      orderBy: this.dataset.orderBy || '',
      awayDefault: this.dataset.awayDefault || '',
      noValidDates: this.dataset.noValidDates || '',
      noAvailableDates: this.dataset.noAvailableDates || '',
      missingInput: this.dataset.missingInput || '',
      loadError: this.dataset.loadError || '',
    };
  }

  formatMessage(template, values = {}) {
    return String(template || '').replace(/\{\{\s*([\w-]+)\s*\}\}/g, (_match, key) => (
      values[key] === undefined || values[key] === null ? '' : String(values[key])
    ));
  }

  formatDateLabel(dateObj, options) {
    if (!dateObj) return '';
    try {
      return new Intl.DateTimeFormat(this.locale || 'en-US', options).format(dateObj);
    } catch (_error) {
      return new Intl.DateTimeFormat('en-US', options).format(dateObj);
    }
  }

  formatMonthLabel(year, month, style = 'short') {
    return this.formatDateLabel(new Date(year, month, 1, 12, 0, 0), { month: style, year: 'numeric' });
  }

  connectedCallback() {
    delete this.dataset.deliveryCalendarReady;
    this.dataset.deliveryCalendarState = 'initializing';
    this.cartForm = this.closest('form');
    this.initCalendar();
  }

  disconnectedCallback() {
    this.pendingCheckout = null;
    delete this.dataset.deliveryCalendarReady;
    this.dataset.deliveryCalendarState = 'disconnected';
    // Clear any pending timeouts to prevent memory leaks / orphaned updates
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = null;
    }
    if (this._navTimeout) {
      clearTimeout(this._navTimeout);
      this._navTimeout = null;
    }
    if (this._settleTimeout) {
      clearTimeout(this._settleTimeout);
      this._settleTimeout = null;
    }
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }

    // Remove form submit listener
    if (this.cartForm && this.handleFormSubmit) {
      this.cartForm.removeEventListener('submit', this.handleFormSubmit);
    }

    // Remove checkout button listeners
    if (this.checkoutButtons && this.handleCheckoutClick) {
      this.checkoutButtons.forEach((btn) => {
        btn.removeEventListener('click', this.handleCheckoutClick);
      });
    }

    // Remove shared state listener
    if (typeof window !== 'undefined' && this.handleSharedSelectionEvent) {
      window.removeEventListener(SHIP_DATE_EVENT_NAME, this.handleSharedSelectionEvent);
      this.handleSharedSelectionEvent = null;
    }
  }

  debug(...args) {
    if (!this.isDebugEnabled) return;
    console.debug('[DeliveryCalendar]:', ...args);
  }

  logWarn(...args) {
    if (this.isTestEnv) return;
    console.warn(...args);
  }

  logError(...args) {
    if (this.isTestEnv) return;
    console.error(...args);
  }

  findAssociatedElementById(id) {
    if (!id) return null;
    const findExactId = (root) => {
      if (!root?.querySelectorAll) return null;
      return Array.from(root.querySelectorAll('[id]')).find((element) => element.id === id) || null;
    };

    return findExactId(this.cartForm)
      || findExactId(this.parentElement)
      || document.getElementById(id);
  }

  markInitializationError(message) {
    delete this.dataset.deliveryCalendarReady;
    this.dataset.deliveryCalendarState = 'error';
    this.innerHTML = '';
    const error = document.createElement('p');
    error.className = 'delivery-calendar-picker-v2__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    this.appendChild(error);
    this.dispatchEvent(new CustomEvent('delivery-calendar:error', {
      bubbles: true,
      detail: { message },
    }));
  }

  broadcastSelection(entry, status = 'pending') {
    if (!entry || typeof window === 'undefined') return;
    const payload = {
      value: entry.date,
      label: entry.label,
      month: entry.dateObj?.getMonth(),
      year: entry.dateObj?.getFullYear(),
      status,
      sourceId: this.calendarInstanceId,
    };
    setSharedShipDateState(payload);
    window.dispatchEvent(new CustomEvent(SHIP_DATE_EVENT_NAME, { detail: payload }));
    // 'pending' marks a fresh shopper selection; 'synced' is a programmatic
    // restore or cart echo and must not double-count as a selection.
    if (status !== 'pending') return;
    const analytics = window.SnowcubesAnalytics;
    if (!analytics || !analytics.EVENTS || typeof analytics.track !== 'function') return;
    analytics.track(analytics.EVENTS.DELIVERY_DATE_SELECT, {
      ship_date: payload.value,
      label: payload.label,
      source: 'calendar',
    });
  }

  parseISODate(iso) {
    if (!iso) return null;
    const [year, month, day] = iso.split('-').map(Number);
    if ([year, month, day].some((value) => Number.isNaN(value))) return null;
    // Use noon to prevent midnight-boundary timezone crossover (Bug #12)
    return new Date(year, month - 1, day, 12, 0, 0);
  }

  formatDateKey(dateObj) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  compareMonths(aYear, aMonth, bYear, bMonth) {
    return (aYear - bYear) * 12 + (aMonth - bMonth);
  }

  buildMonthGrid(year, month) {
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    // Leading empty cells
    for (let i = 0; i < startOffset; i += 1) {
      cells.push(null);
    }

    // Day cells - use noon to prevent midnight-boundary timezone crossover (Bug #12)
    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month, day, 12, 0, 0);
      cells.push({ dateObj, dateKey: this.formatDateKey(dateObj) });
    }

    // Pad to exactly 42 cells (6 rows × 7 cols) - prevents height jumping between months
    while (cells.length < 42) {
      cells.push(null);
    }

    return cells;
  }

  isISODateString(value) {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
  }

  parseDateList(value) {
    if (!value || typeof value !== 'string') return [];
    return value
      .split(/[,\s]+/)
      .map((item) => item.trim())
      .filter((item) => this.isISODateString(item));
  }

  buildDeliveryLabel(dateObj) {
    return this.formatDateLabel(dateObj, { weekday: 'short', month: 'long', day: 'numeric' });
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[&<>"']/g, (char) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return entities[char] || char;
    });
  }

  normalizeOverrideConfig(rawConfig = {}) {
    const includeMap = new Map();
    const excludeMap = new Map();

    if (!rawConfig || typeof rawConfig !== 'object') {
      return { includeMap, excludeMap, holidayMap: new Map() };
    }

    const includeDates = Array.isArray(rawConfig.include_dates) ? rawConfig.include_dates : [];
    includeDates.forEach((entry) => {
      if (typeof entry === 'string') {
        if (!this.isISODateString(entry)) return;
        const date = entry.trim();
        includeMap.set(date, { date, cutoff: null });
        return;
      }

      if (!entry || typeof entry !== 'object' || !this.isISODateString(entry.date)) return;

      const date = entry.date.trim();
      const cutoff = this.isISODateString(entry.cutoff) ? entry.cutoff.trim() : null;

      includeMap.set(date, { date, cutoff });
    });

    const excludeDates = Array.isArray(rawConfig.exclude_dates) ? rawConfig.exclude_dates : [];
    excludeDates.forEach((entry) => {
      if (typeof entry === 'string') {
        if (!this.isISODateString(entry)) return;
        const date = entry.trim();
        excludeMap.set(date, { date, reason: 'blocked', message: '' });
        return;
      }

      if (!entry || typeof entry !== 'object' || !this.isISODateString(entry.date)) return;

      const date = entry.date.trim();
      const reason = typeof entry.reason === 'string' && entry.reason.trim()
        ? entry.reason.trim().toLowerCase()
        : 'blocked';
      const message = typeof entry.message === 'string' ? entry.message.trim() : '';

      excludeMap.set(date, { date, reason, message });
    });

    const holidayMap = new Map();
    const holidays = Array.isArray(rawConfig.holidays) ? rawConfig.holidays : [];
    holidays.forEach((entry) => {
      if (!entry || typeof entry !== 'object' || !this.isISODateString(entry.date)) return;
      const date = entry.date.trim();
      // Support both new format { label, color } and legacy { theme }
      const label = (typeof entry.label === 'string' && entry.label.trim()) || (typeof entry.theme === 'string' && entry.theme.trim()) || null;
      const color = (typeof entry.color === 'string' && entry.color.trim()) || '#c97b7b';
      if (label) holidayMap.set(date, { date, label, color });
    });

    return { includeMap, excludeMap, holidayMap };
  }

  getOverridesConfigFromScript() {
    const overridesScriptId = this.dataset.overridesId || '';
    if (!overridesScriptId) return null;

    const overridesScript = this.findAssociatedElementById(overridesScriptId);
    if (!overridesScript) {
      this.logWarn('Delivery calendar overrides script not found:', overridesScriptId);
      return null;
    }

    try {
      const parsed = JSON.parse(overridesScript.textContent || '{}');
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (error) {
      this.logWarn('Unable to parse delivery overrides JSON, falling back to data attributes', error);
      return null;
    }
  }

  getNormalizedOverrides() {
    const scriptConfig = this.getOverridesConfigFromScript();
    if (scriptConfig) {
      return this.normalizeOverrideConfig(scriptConfig);
    }

    return this.normalizeOverrideConfig({
      include_dates: this.parseDateList(this.dataset.includeDates || ''),
      exclude_dates: this.parseDateList(this.dataset.excludeDates || ''),
    });
  }

  applyDateOverrides(deliveryDatesRaw, overrides = {}) {
    const normalizedSource = Array.isArray(deliveryDatesRaw) ? deliveryDatesRaw : [];
    const includeMap = overrides.includeMap instanceof Map ? overrides.includeMap : new Map();
    const excludeMap = overrides.excludeMap instanceof Map ? overrides.excludeMap : new Map();
    const mergedByDate = new Map();

    normalizedSource.forEach((entry) => {
      if (!entry || !this.isISODateString(entry.date)) return;
      const date = entry.date.trim();
      mergedByDate.set(date, {
        ...entry,
        date,
        label: entry.label || this.buildDeliveryLabel(this.parseISODate(date)),
      });
    });

    includeMap.forEach((metadata, date) => {
      const existing = mergedByDate.get(date);
      if (existing) {
        existing.cutoffDate = metadata.cutoff || null;
        return;
      }
      const dateObj = this.parseISODate(date);
      if (!dateObj) return;
      mergedByDate.set(date, {
        date,
        label: this.buildDeliveryLabel(dateObj),
        badge: null,
        cutoffDate: metadata.cutoff || null,
      });
    });

    excludeMap.forEach((metadata, date) => {
      if (mergedByDate.has(date)) return;
      const dateObj = this.parseISODate(date);
      if (!dateObj) return;
      mergedByDate.set(date, {
        date,
        label: this.buildDeliveryLabel(dateObj),
        badge: null,
      });
    });

    mergedByDate.forEach((entry, date) => {
      const exclusion = excludeMap.get(date);
      if (!exclusion) return;

      const reason = exclusion.reason || 'blocked';
      const isAway = reason === 'away';
      const message = exclusion.message || (isAway ? this.localeCopy.awayDefault || DeliveryCalendarPicker.DEFAULT_AWAY_MESSAGE : '');

      entry.disabled = true;
      entry.isAway = isAway;
      entry.awayReason = reason;
      if (message) {
        entry.awayMessage = message;
      }
    });

    const sorted = Array.from(mergedByDate.values()).sort((a, b) => {
      const aDate = this.parseISODate(a.date);
      const bDate = this.parseISODate(b.date);
      if (!aDate || !bDate) return 0;
      return aDate - bDate;
    });

    sorted.forEach((entry) => {
      entry.badge = null;
    });
    const firstAvailable = sorted.find((entry) => !entry.disabled);
    if (firstAvailable) {
      firstAvailable.badge = this.localeCopy.nextAvailable || null;
    }

    return sorted;
  }

  resolveDeliverableSelection(entry, fallbackEntry) {
    if (!entry) return null;
    if (this.isDeliverable(entry)) return entry;

    if (
      fallbackEntry
      && fallbackEntry.date !== entry.date
      && this.isDeliverable(fallbackEntry)
    ) {
      return fallbackEntry;
    }

    return null;
  }

  isDeliverable(entry) {
    if (!entry) return false;
    // Recalculate todayStart fresh to avoid stale captured value (Bug #14)
    const now = new Date();
    const freshTodayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (entry.dateObj < freshTodayStart) return false;
    if (entry.disabled) return false;
    if (this.isPastCutoff(entry.dateObj, entry.cutoffDate)) return false;
    return true;
  }

  isPastCutoff(dateObj, cutoffOverride) {
    if (!dateObj) return false;

    let cutoffYear, cutoffMonth, cutoffDay;

    if (cutoffOverride && this.isISODateString(cutoffOverride)) {
      const parsed = cutoffOverride.trim().split('-').map(Number);
      cutoffYear = parsed[0];
      cutoffMonth = parsed[1] - 1;
      cutoffDay = parsed[2];
    } else {
      // parseISODate builds dateObj at LOCAL noon (new Date(y, m-1, d, 12, 0, 0)),
      // so read the calendar fields with LOCAL getters. Using getUTC* here shifted
      // the ship day by -1 for viewers at extreme positive UTC offsets.
      const shipYear = dateObj.getFullYear();
      const shipMonth = dateObj.getMonth();
      const shipDay = dateObj.getDate();
      const cutoffDate = new Date(Date.UTC(shipYear, shipMonth, shipDay));
      // Lock each date `leadTimeDays` before it ships — NOT a hardcoded 3.
      // generateDeliveryDates already offers dates `leadTimeDays` out; the
      // cutoff must use the SAME window or a merchant lead time != 3 lets a
      // shopper order a date too close to ship to fulfill (lead > 3) or blocks a
      // still-orderable date (lead < 3). Falls back to the default if unset.
      const leadDays = Number.isFinite(this.leadTimeDays) && this.leadTimeDays >= 0
        ? this.leadTimeDays
        : this.DEFAULT_LEAD_TIME_DAYS;
      cutoffDate.setUTCDate(cutoffDate.getUTCDate() - leadDays);
      cutoffYear = cutoffDate.getUTCFullYear();
      cutoffMonth = cutoffDate.getUTCMonth();
      cutoffDay = cutoffDate.getUTCDate();
    }

    const cutoffUtc = this.getEasternUtcTimestamp(cutoffYear, cutoffMonth, cutoffDay, this.cutoffHour, this.cutoffMinute);
    // Use fresh Date.now() instead of stale captured nowUtc (Bug #14)
    return Date.now() >= cutoffUtc;
  }

  getEasternUtcTimestamp(year, monthIndex, day, hour = 0, minute = 0) {
    const offsetMinutes = this.getEasternOffsetMinutes(year, monthIndex, day);
    const baseUtc = Date.UTC(year, monthIndex, day, hour, minute, 0);
    return baseUtc - offsetMinutes * 60 * 1000;
  }

  getEasternOffsetMinutes(year, monthIndex, day) {
    return this.isEasternDateInDst(year, monthIndex, day) ? -240 : -300;
  }

  isEasternDateInDst(year, monthIndex, day) {
    const marchFirst = new Date(Date.UTC(year, 2, 1));
    const marchDay = marchFirst.getUTCDay();
    const firstSundayInMarch = ((7 - marchDay) % 7) + 1;
    const secondSundayInMarch = firstSundayInMarch + 7;

    const novemberFirst = new Date(Date.UTC(year, 10, 1));
    const novemberDay = novemberFirst.getUTCDay();
    const firstSundayInNovember = ((7 - novemberDay) % 7) + 1;

    const current = Date.UTC(year, monthIndex, day);
    const dstStart = Date.UTC(year, 2, secondSundayInMarch);
    const dstEnd = Date.UTC(year, 10, firstSundayInNovember);

    return current >= dstStart && current < dstEnd;
  }

  createShell(copy) {
    const container = document.createElement('div');
    container.className = 'delivery-calendar-picker-v2__shell';
    const prevMonth = this.escapeHtml(copy.prevMonth);
    const nextMonth = this.escapeHtml(copy.nextMonth);
    const jumpToSelected = this.escapeHtml(copy.jumpToSelected);
    if (copy.heading) {
      container.setAttribute('role', 'group');
      container.setAttribute('aria-label', copy.heading);
    }
    container.innerHTML = `
      ${copy.weekdayTag ? `<div class="delivery-calendar-picker-v2__weekday-pill" data-calendar-weekday-pill>${this.escapeHtml(copy.weekdayTag)}</div>` : ''}
      <div class="delivery-calendar-picker-v2__navigation">
        <button type="button" class="delivery-calendar-nav__prev" aria-label="${prevMonth}" data-calendar-prev>
          <span aria-hidden="true">‹</span>
        </button>
        <div class="delivery-calendar-nav__center">
          <div class="delivery-calendar-nav__month" data-calendar-month></div>
          <div class="delivery-calendar-nav__ship-date" data-calendar-ship-date role="button" tabindex="0" aria-label="${jumpToSelected}"></div>
          <div class="delivery-calendar-nav__order-by" data-calendar-order-by hidden></div>
        </div>
        <button type="button" class="delivery-calendar-nav__next" aria-label="${nextMonth}" data-calendar-next>
          <span aria-hidden="true">›</span>
        </button>
      </div>
      <div class="delivery-calendar-picker-v2__weekdays">
        ${this.weekdayNames.map((day) => `<span>${day}</span>`).join('')}
      </div>
      <div class="delivery-calendar-picker-v2__carousel" data-calendar-carousel>
        <div class="delivery-calendar-picker-v2__dates" data-calendar-panel="prev"></div>
        <div class="delivery-calendar-picker-v2__dates" data-calendar-panel="current"></div>
        <div class="delivery-calendar-picker-v2__dates" data-calendar-panel="next"></div>
      </div>
      <div class="delivery-calendar-picker-v2__events" data-calendar-events hidden></div>
    `;

    return {
      shell: container,
      shipDateEl: container.querySelector('[data-calendar-ship-date]'),
      orderByEl: container.querySelector('[data-calendar-order-by]'),
      prevBtn: container.querySelector('[data-calendar-prev]'),
      nextBtn: container.querySelector('[data-calendar-next]'),
      monthLabel: container.querySelector('[data-calendar-month]'),
      carousel: container.querySelector('[data-calendar-carousel]'),
      panels: {
        prev: container.querySelector('[data-calendar-panel="prev"]'),
        current: container.querySelector('[data-calendar-panel="current"]'),
        next: container.querySelector('[data-calendar-panel="next"]'),
      },
      events: container.querySelector('[data-calendar-events]'),
    };
  }

  generateDeliveryDates(deliveryWeekday, leadTimeDays, rangeWeeks) {
    const dates = [];
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + (rangeWeeks * 7)); // 8 weeks = 56 days

    // Use noon to prevent midnight-boundary timezone crossover (Bug #12)
    let currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0);
    const daysUntilDelivery = (deliveryWeekday - today.getDay() + 7) % 7;

    if (daysUntilDelivery < leadTimeDays) {
      currentDate.setDate(currentDate.getDate() + daysUntilDelivery + 7);
    } else {
      currentDate.setDate(currentDate.getDate() + daysUntilDelivery);
    }

    let isFirst = true;
    while (currentDate <= endDate) {
      const label = this.buildDeliveryLabel(currentDate);

      dates.push({
        date: this.formatDateKey(currentDate),
        label,
        badge: isFirst ? (this.localeCopy.nextAvailable || null) : null
      });

      isFirst = false;
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7, 12, 0, 0);
    }

    return dates;
  }

  async updateCartAttributes(dateString, label) {
    // Optimistic UI: selection already shown, this saves in background
    const generation = this.uniqueId;
    const selectionGeneration = this.selectionGeneration;
    const outcome = await writeShipDate(dateString);
    if (!this.isConnected || generation !== this.uniqueId || selectionGeneration !== this.selectionGeneration) return false;
    if (outcome === 'saved') {
      this.debug('Cart updated with ship date', dateString);
      this.classList.remove('delivery-calendar--save-error');
    } else if (outcome === 'failed' || outcome === 'indeterminate') {
      // All retries failed - signal error to UI
      this.logError('Cart ship date is unconfirmed:', dateString);
      this.classList.add('delivery-calendar--save-error');
      this.dispatchEvent(new CustomEvent('cart-save-error', { bubbles: true }));
    }
    return outcome === 'saved';
  }

  initCalendar() {
    // Generate unique ID for this instance
    this.uniqueId = `cal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.setLocaleData();

    const dataScriptId = this.dataset.datesId || 'delivery-dates-data';
    const dataScript = this.findAssociatedElementById(dataScriptId);

    // Read configuration from data attributes
    const deliveryWeekday = this.dataset.deliveryWeekday
      ? parseInt(this.dataset.deliveryWeekday, 10)
      : this.DEFAULT_DELIVERY_WEEKDAY;
    const leadTimeDays = this.dataset.leadTimeDays
      ? parseInt(this.dataset.leadTimeDays, 10)
      : this.DEFAULT_LEAD_TIME_DAYS;
    // Persist the active lead time so isPastCutoff locks each date at
    // (shipDate − leadTimeDays), not a hardcoded 3. Guard against a NaN/negative
    // data attribute so the cutoff math can't produce an Invalid Date.
    this.leadTimeDays = Number.isFinite(leadTimeDays) && leadTimeDays >= 0
      ? leadTimeDays
      : this.DEFAULT_LEAD_TIME_DAYS;
    const rangeWeeks = this.dataset.rangeWeeks
      ? parseInt(this.dataset.rangeWeeks, 10)
      : this.DEFAULT_DATE_RANGE_WEEKS;
    // Parse cutoff time from data attribute (format: "HH:MM", default "15:00")
    const cutoffTimeRaw = this.dataset.cutoffTime || '';
    if (cutoffTimeRaw) {
      const parts = cutoffTimeRaw.split(':').map(Number);
      if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        this.cutoffHour = parts[0];
        this.cutoffMinute = parts[1];
      }
    }

    const overrides = this.getNormalizedOverrides();

    let deliveryDatesRaw = [];

    if (dataScript) {
      try {
        deliveryDatesRaw = JSON.parse(dataScript.textContent || '[]');
      } catch (error) {
        this.logWarn('Unable to parse delivery dates JSON, generating dynamically', error);
      }
    }

    if (!Array.isArray(deliveryDatesRaw) || deliveryDatesRaw.length === 0) {
      this.debug('Generating delivery dates dynamically', { deliveryWeekday, leadTimeDays, rangeWeeks });
      deliveryDatesRaw = this.generateDeliveryDates(deliveryWeekday, leadTimeDays, rangeWeeks);
    }

    deliveryDatesRaw = this.applyDateOverrides(deliveryDatesRaw, overrides);

    const deliveryDates = deliveryDatesRaw
      .map((entry) => {
        const dateObj = this.parseISODate(entry.date);
        if (!dateObj) return null;
        return {
          ...entry,
          dateObj,
          dateKey: this.formatDateKey(dateObj),
          dayNumber: dateObj.getDate(),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.dateObj - b.dateObj);

    if (deliveryDates.length === 0) {
      this.logError('Delivery calendar: no valid dates found after parsing');
      this.markInitializationError(this.localeCopy.noValidDates);
      return;
    }

    const firstDate = deliveryDates[0].dateObj;
    const lastDate = deliveryDates[deliveryDates.length - 1].dateObj;

    // Find first and last AVAILABLE dates (not just dates in range)
    // This prevents showing empty months with no selectable dates
    const firstAvailableDate = deliveryDates.find((entry) => this.isDeliverable(entry));
    const lastAvailableDate = [...deliveryDates].reverse().find((entry) => this.isDeliverable(entry));

    // Bug #16: If all dates exist but none are deliverable, show empty message
    if (!firstAvailableDate && deliveryDates.length > 0) {
      this.markInitializationError(this.localeCopy.noAvailableDates);
      return;
    }

    // Use first/last AVAILABLE date's month as boundaries (not just first/last date in range)
    const minMonthYear = firstAvailableDate
      ? { month: firstAvailableDate.dateObj.getMonth(), year: firstAvailableDate.dateObj.getFullYear() }
      : { month: firstDate.getMonth(), year: firstDate.getFullYear() };
    const maxMonthYear = lastAvailableDate
      ? { month: lastAvailableDate.dateObj.getMonth(), year: lastAvailableDate.dateObj.getFullYear() }
      : { month: lastDate.getMonth(), year: lastDate.getFullYear() };

    const clampMonthYear = (year, month) => {
      if (this.compareMonths(year, month, minMonthYear.year, minMonthYear.month) < 0) {
        return { ...minMonthYear };
      }
      if (this.compareMonths(year, month, maxMonthYear.year, maxMonthYear.month) > 0) {
        return { ...maxMonthYear };
      }
      return { year, month };
    };

    const copy = {
      heading: this.localeCopy.heading,
      // No fallback sentence on purpose. This used to carry a full transcription
      // of the ship-cutoff copy, which meant the same customer-facing promise
      // lived in two languages and drifted whenever only one was edited. The
      // sentence now has one home, snippets/delivery-calendar-picker-v2.liquid,
      // and reaches here through data-description.
      //
      // Every consumer sets that attribute (the snippet at :34, and
      // sections/component-playground.liquid at :302 — the only two, verified
      // 2026-08-14). A consumer that omits it renders no description, which is
      // the honest outcome: better a missing line than a stale promise about
      // when an order has to be placed.
      description: this.dataset.description || '',
      summaryDefault: this.localeCopy.summaryDefault.trim(),
      weekdayTag: this.dataset.weekdayTag || '',
      prevMonth: this.localeCopy.prevMonth,
      nextMonth: this.localeCopy.nextMonth,
      jumpToSelected: this.localeCopy.jumpToSelected,
      unavailable: this.localeCopy.unavailable,
      nextAvailable: this.localeCopy.nextAvailable,
      ships: this.localeCopy.ships,
      orderBy: this.localeCopy.orderBy,
    };

    const requiredMessage = this.localeCopy.required;
    const dateInputId = this.dataset.dateInputId || 'delivery-date';
    const errorId = this.dataset.errorId || 'calendar-error';

    let errorEl = Array.from(this.querySelectorAll('[id]')).find((element) => element.id === errorId) || null;
    const { shell, shipDateEl, orderByEl, prevBtn, nextBtn, monthLabel, carousel, panels, events } = this.createShell(copy);

    this.innerHTML = '';
    this.appendChild(shell);

    if (errorEl) {
      this.appendChild(errorEl);
    } else {
      errorEl = document.createElement('p');
      errorEl.id = errorId;
      errorEl.className = 'delivery-calendar-picker-v2__error';
      errorEl.hidden = true;
      this.appendChild(errorEl);
    }

    const dateInput = this.findAssociatedElementById(dateInputId);

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.hidden = !message;
      errorEl.textContent = message || '';
    };
    this.showRequiredError = () => {
      showError(requiredMessage);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    if (!dateInput) {
      this.markInitializationError(this.localeCopy.missingInput);
      return;
    }

    const dateMap = deliveryDates.reduce((acc, entry) => {
      acc[entry.dateKey] = entry;
      return acc;
    }, {});

    // After the cart emptied, a server-rendered date may be the ended order's:
    // do not restore it until a new pick, and retry a clear that has not landed.
    const reset = getShipDateReset();
    if (reset?.pending && reset.value && !shipDateWriter.latest) {
      shipDateWriter.latest = {
        value: reset.value, token: reset.token, resolve: () => {}, revision: ++shipDateWriter.revision,
      };
      setSharedShipDateState({ value: reset.value, status: 'pending' });
    }
    if (reset?.ended) shipDateWriter.orderEnded = true;
    if (reset?.pending) shipDateWriter.clearPending = true;
    const savedDate = reset?.pending && reset.value ? reset.value
      : dateInput.value && !shipDateWriter.orderEnded && !reset?.pending ? dateInput.value : null;
    const savedSelection = savedDate ? deliveryDates.find((entry) => entry.date === savedDate) : null;
    // Reuse firstAvailableDate from above (already calculated for minMonthYear)
    const defaultSelection = savedSelection || firstAvailableDate || deliveryDates[0];

    // Start on minMonthYear (guaranteed to have available dates)
    const initialMonth = { month: minMonthYear.month, year: minMonthYear.year };

    // Minimal state: just what we need for rendering
    const state = {
      month: initialMonth.month,
      year: initialMonth.year,
      selectedDate: defaultSelection?.date || null, // ISO string e.g. "2025-12-08"
    };

    this.debug('Init calendar', {
      totalDates: deliveryDates.length,
      rangeStart: firstDate.toISOString().slice(0, 10),
      rangeEnd: lastDate.toISOString().slice(0, 10),
      currentMonth: `${this.monthNames[state.month]} ${state.year}`,
      defaultSelection: defaultSelection?.dateKey,
    });

    const formatShortDate = (iso) => {
      const d = this.parseISODate(iso);
      return d ? this.formatDateLabel(d, { month: 'short', day: 'numeric' }) : iso;
    };

    // Update the ship date display in the nav bar
    const updateShipDateDisplay = (shortText = '') => {
      if (shipDateEl) {
        shipDateEl.textContent = shortText;
        shipDateEl.hidden = !shortText;
      }
    };

    // An explicit early cutoff on the selected date shows in the month row.
    // The regular Friday cutoff is already the description line.
    const updateOrderBy = (entry) => {
      if (!orderByEl) return;
      const text = entry && entry.cutoffDate
        ? this.formatMessage(copy.orderBy, { date: formatShortDate(entry.cutoffDate) })
        : '';
      orderByEl.textContent = text;
      orderByEl.hidden = !text;
    };

    const applySelection = async (entry, { skipRender, skipCartUpdate, broadcast = true } = {}) => {
      let selectedEntry = entry;
      if (!selectedEntry) return;

      // Guard against stale or non-deliverable selections (e.g., old cart state)
      const resolvedSelection = this.resolveDeliverableSelection(
        selectedEntry,
        firstAvailableDate
      );
      if (!resolvedSelection) {
        this.debug('Selection not deliverable and no fallback available', { requested: selectedEntry.date });
        return;
      }
      if (resolvedSelection.date !== selectedEntry.date) {
        this.debug('Selection not deliverable, falling back to first available', {
          requested: selectedEntry.date,
          fallback: resolvedSelection.date,
        });
      }
      selectedEntry = resolvedSelection;

      const entryMonth = selectedEntry.dateObj?.getMonth();
      const entryYear = selectedEntry.dateObj?.getFullYear();
      if (typeof entryMonth === 'number' && typeof entryYear === 'number') {
        state.month = entryMonth;
        state.year = entryYear;
      }

      state.selectedDate = selectedEntry.date;
      this.selectionGeneration += 1;
      if (dateInput) dateInput.value = selectedEntry.date;

      updateNav();

      // Update UI immediately - don't wait for server
      if (!skipRender) {
        renderGrid();
      }

      // Update summary immediately with selected date
      const monthShort = this.monthNamesShort[selectedEntry.dateObj.getMonth()];
      const day = selectedEntry.dateObj.getDate();
      updateShipDateDisplay(this.formatMessage(copy.ships, { month: monthShort, day }));
      updateOrderBy(selectedEntry);

      showError('');
      this.debug('Applied selection', { date: selectedEntry.date, label: selectedEntry.label });

      if (broadcast) {
        this.broadcastSelection(selectedEntry, skipCartUpdate ? 'synced' : 'pending');
      }

      // Save to cart in background (don't block UI)
      if (!skipCartUpdate) {
        // Fire and forget - updateCartAttributes handles its own UI feedback
        this.updateCartAttributes(selectedEntry.date, selectedEntry.label);
      }
    };

    // Helper to render a single month panel
    const renderMonthPanel = (panel, year, month) => {
      if (!panel) return;
      const cells = this.buildMonthGrid(year, month);
      panel.innerHTML = '';

      cells.forEach((cell) => {
        const cellEl = document.createElement('div');
        cellEl.className = 'delivery-calendar-picker-v2__cell';

        if (!cell) {
          cellEl.classList.add('delivery-calendar-picker-v2__cell--empty');
          panel.appendChild(cellEl);
          return;
        }

        cellEl.setAttribute('data-date-key', cell.dateKey);

        const entry = dateMap[cell.dateKey];
        if (!entry) {
          cellEl.classList.add('delivery-calendar-picker-v2__cell--no-slot');
          const holidayEntry = overrides.holidayMap && overrides.holidayMap.get(cell.dateKey);
          if (holidayEntry) {
            cellEl.classList.add('is-holiday');
            cellEl.style.border = `1.5px solid ${holidayEntry.color}`;
            cellEl.style.borderRadius = 'var(--calendar-cell-radius, 12px)';
            cellEl.style.position = 'relative';
          }
          cellEl.innerHTML = `<span class="delivery-date__number">${cell.dateObj.getDate()}</span>`;
          panel.appendChild(cellEl);
          return;
        }

        const available = this.isDeliverable(entry);
        if (available) {
          cellEl.classList.add('is-available');
        } else {
          cellEl.classList.add('is-disabled');
        }
        if (entry.isAway) {
          cellEl.classList.add('is-away');
        }
        const isSelected = entry.date === state.selectedDate;
        if (isSelected) {
          cellEl.classList.add('is-selected');
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'delivery-date__button';
        button.disabled = !available;
        button.setAttribute('data-date-key', entry.dateKey);

        const awayMessage = entry.awayMessage || this.localeCopy.awayDefault || DeliveryCalendarPicker.DEFAULT_AWAY_MESSAGE;
        const unavailableLabel = this.formatMessage(copy.unavailable);
        const unavailableMessage = !awayMessage ||
          awayMessage.trim().replace(/[.。]$/, '') === unavailableLabel.trim().replace(/[.。]$/, '')
          ? unavailableLabel
          : `${unavailableLabel}: ${awayMessage}`;
        const srOnlyMarkup = entry.isAway
          ? `<span class="visually-hidden">${this.escapeHtml(unavailableMessage)}</span>`
          : '';

        button.innerHTML = `
          <span class="delivery-date__number">${cell.dateObj.getDate()}</span>${srOnlyMarkup}
        `;
        if (entry.isAway) {
          button.setAttribute('aria-label', `${entry.label}. ${unavailableMessage}`);
        } else {
          button.setAttribute('aria-label', entry.label);
        }
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        if (available) {
          button.addEventListener('click', () => applySelection(entry));
        }

        cellEl.appendChild(button);
        panel.appendChild(cellEl);
      });
    };

    // Get adjacent month (handles year wrap)
    const getAdjacentMonth = (year, month, offset) => {
      let newMonth = month + offset;
      let newYear = year;
      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      return { year: newYear, month: newMonth };
    };

    // Render carousel panels - only render months within valid range
    const renderGrid = () => {
      if (!panels.current) return;

      const prev = getAdjacentMonth(state.year, state.month, -1);
      const next = getAdjacentMonth(state.year, state.month, 1);

      // Check boundaries
      const isAtMin = this.compareMonths(state.year, state.month, minMonthYear.year, minMonthYear.month) <= 0;
      const isAtMax = this.compareMonths(state.year, state.month, maxMonthYear.year, maxMonthYear.month) >= 0;

      // Render or collapse panels based on boundaries
      if (isAtMin) {
        panels.prev.innerHTML = '';
        panels.prev.style.display = 'none'; // Fully collapse - no scroll space
      } else {
        panels.prev.style.display = '';
        renderMonthPanel(panels.prev, prev.year, prev.month);
      }

      renderMonthPanel(panels.current, state.year, state.month);

      if (isAtMax) {
        panels.next.innerHTML = '';
        panels.next.style.display = 'none'; // Fully collapse - no scroll space
      } else {
        panels.next.style.display = '';
        renderMonthPanel(panels.next, next.year, next.month);
      }

      // Scroll to appropriate position (defer to ensure layout is complete)
      if (carousel) {
        requestAnimationFrame(() => {
          const width = carousel.offsetWidth;
          if (!width) {
            return; // Element likely hidden; will re-center when visible
          }
          if (isAtMin) {
            carousel.scrollLeft = 0; // Current is first panel
          } else {
            carousel.scrollLeft = width; // Current is center panel
          }
        });
      }
    };

    const updateNav = () => {
      if (monthLabel) {
        monthLabel.textContent = this.formatMonthLabel(state.year, state.month, 'short');
      }
      if (prevBtn) {
        prevBtn.disabled = this.compareMonths(state.year, state.month, minMonthYear.year, minMonthYear.month) <= 0;
      }
      if (nextBtn) {
        nextBtn.disabled = this.compareMonths(state.year, state.month, maxMonthYear.year, maxMonthYear.month) >= 0;
      }

      // Ship date label always visible when there's a selection (can't deselect)
      // Only hide if there's no content at all
      if (shipDateEl) {
        shipDateEl.hidden = !shipDateEl.textContent;
      }
    };

    // Render description + upcoming holidays
    if (events) {
      const now = new Date();
      const todayKey = this.formatDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0));
      const allEvents = [];

      // Holidays
      if (overrides.holidayMap && overrides.holidayMap.size > 0) {
        overrides.holidayMap.forEach((h) => {
          if (h.date >= todayKey) {
            allEvents.push({ date: h.date, label: h.label, color: h.color });
          }
        });
      }

      allEvents.sort((a, b) => a.date.localeCompare(b.date));

      const descriptionHtml = copy.description
        ? `<p class="delivery-calendar-picker-v2__footer-desc">${copy.description}</p>`
        : '';

      if (allEvents.length > 0) {
        const items = allEvents.map((ev) => `<li class="delivery-calendar-picker-v2__event">
              <span class="delivery-calendar-picker-v2__event-dot" style="background:${this.escapeHtml(ev.color)}"></span>
              <span class="delivery-calendar-picker-v2__event-date">${this.escapeHtml(formatShortDate(ev.date))}</span>
              <span>${this.escapeHtml(ev.label)}</span>
            </li>`).join('');
        events.innerHTML = `${descriptionHtml}<ul class="delivery-calendar-picker-v2__events-list">${items}</ul>`;
      } else if (descriptionHtml) {
        events.innerHTML = descriptionHtml;
      }

      if (events.innerHTML) events.hidden = false;
    }

    // Navigation lock - prevents rapid clicks and scroll handler conflicts
    let isNavigating = false;
    let emergencyUnlockTimer = null;

    /**
     * Set navigation lock with emergency unlock failsafe.
     * If lock isn't released within NAV_LOCK_TIMEOUT_MS, force unlock to prevent stuck state.
     */
    const setNavigationLock = (locked) => {
      isNavigating = locked;
      if (emergencyUnlockTimer) {
        clearTimeout(emergencyUnlockTimer);
        emergencyUnlockTimer = null;
      }
      if (locked) {
        emergencyUnlockTimer = setTimeout(() => {
          if (isNavigating) {
            this.debug('Emergency navigation unlock triggered');
            isNavigating = false;
          }
          emergencyUnlockTimer = null;
        }, DeliveryCalendarPicker.NAV_LOCK_TIMEOUT_MS);
      }
    };

    /**
     * Navigate to adjacent month via button click.
     * Flow: scroll to panel → wait for animation → update state → re-center
     */
    const navigateMonth = (direction) => {
      if (isNavigating) return;

      const target = getAdjacentMonth(state.year, state.month, direction);
      const clamped = clampMonthYear(target.year, target.month);

      // Check if navigation would actually change anything
      if (clamped.year === state.year && clamped.month === state.month) {
        this.debug('Navigation blocked - at boundary', { direction });
        return;
      }

      setNavigationLock(true);

      // Clear any pending timeouts from previous navigation
      if (this._navTimeout) clearTimeout(this._navTimeout);

      if (carousel) {
        // Scroll to adjacent panel (prev = 0, next = 2 * panelWidth)
        const targetScroll = direction < 0 ? 0 : carousel.offsetWidth * 2;
        carousel.scrollTo({ left: targetScroll, behavior: 'smooth' });

        // After scroll animation completes, update state and re-center
        this._navTimeout = setTimeout(() => {
          state.month = clamped.month;
          state.year = clamped.year;
          updateNav();
          renderGrid(); // Re-renders panels and snaps to center

          // Allow next navigation after panels settle
          this._settleTimeout = setTimeout(() => {
            setNavigationLock(false);
          }, DeliveryCalendarPicker.SCROLL_SETTLE_MS);
        }, DeliveryCalendarPicker.SCROLL_ANIMATION_MS);
      } else {
        // Fallback: no carousel element
        state.month = clamped.month;
        state.year = clamped.year;
        updateNav();
        renderGrid();
        setNavigationLock(false);
      }

      this.debug('Navigated to month', { year: clamped.year, month: clamped.month, direction });
    };

    /**
     * Handle native swipe/scroll on carousel.
     * Trust CSS scroll-snap to handle snapping - we just detect final position.
     * Uses scrollend event (modern browsers) with debounce fallback.
     */
    const onScrollEnd = () => {
      if (!carousel || isNavigating) return;

      const scrollPos = carousel.scrollLeft;
      const panelWidth = carousel.offsetWidth;
      const panelIndex = Math.round(scrollPos / panelWidth);

      // Check actual panel visibility (single source of truth from renderGrid)
      const hasPrev = panels.prev.style.display !== 'none';
      const hasNext = panels.next.style.display !== 'none';

      // Current panel index based on actual layout
      const currentPanelIndex = hasPrev ? 1 : 0;

      // Determine direction based on where we landed vs where current is
      let direction = 0;
      if (panelIndex < currentPanelIndex && hasPrev) {
        direction = -1; // Swiped to prev
      } else if (panelIndex > currentPanelIndex && hasNext) {
        direction = 1; // Swiped to next
      }

      if (direction === 0) return; // No change

      const target = getAdjacentMonth(state.year, state.month, direction);
      const clamped = clampMonthYear(target.year, target.month);

      // Only update if actually changing month
      if (clamped.year === state.year && clamped.month === state.month) return;

      setNavigationLock(true);
      state.month = clamped.month;
      state.year = clamped.year;
      updateNav();
      renderGrid(); // Re-centers carousel

      // Release lock after settle
      this._navTimeout = setTimeout(() => {
        setNavigationLock(false);
      }, DeliveryCalendarPicker.SCROLL_SETTLE_MS);
    };

    if (carousel) {
      // Modern browsers: use scrollend (fires once, no debounce needed)
      // Fallback: debounced scroll event for older browsers
      const supportsScrollend = 'onscrollend' in window;

      if (supportsScrollend) {
        carousel.addEventListener('scrollend', onScrollEnd, { passive: true });
      } else {
        // Fallback with debounce
        const handleCarouselScroll = () => {
          if (this._scrollTimeout) clearTimeout(this._scrollTimeout);
          this._scrollTimeout = setTimeout(onScrollEnd, DeliveryCalendarPicker.SCROLL_DEBOUNCE_MS);
        };
        carousel.addEventListener('scroll', handleCarouselScroll, { passive: true });
      }
      // Note: CSS scroll-snap handles snapping to panels.
      // We removed manual swipe assist - it was fighting with scroll-snap.
    }

    // If component mounts while hidden (e.g., in drawer), re-center once visible
    if (carousel && typeof IntersectionObserver !== 'undefined') {
      const visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          renderGrid(); // Triggers recalc with correct offsetWidth
          visibilityObserver.disconnect();
          this._visibilityObserver = null;
        }
      }, { threshold: 0.01 });
      visibilityObserver.observe(this);
      this._visibilityObserver = visibilityObserver;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => navigateMonth(-1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => navigateMonth(1));
    }

    // Click on ship date label to jump back to selected date's month
    const jumpToSelectedMonth = () => {
      if (isNavigating) return; // Prevent conflicts with ongoing scroll/nav
      const selectedEntry = state.selectedDate ? dateMap[state.selectedDate] : null;
      if (selectedEntry && selectedEntry.dateObj) {
        const targetMonth = selectedEntry.dateObj.getMonth();
        const targetYear = selectedEntry.dateObj.getFullYear();
        if (targetMonth !== state.month || targetYear !== state.year) {
          setNavigationLock(true);
          state.month = targetMonth;
          state.year = targetYear;
          updateNav();
          renderGrid();
          // Release lock after settle
          this._navTimeout = setTimeout(() => {
            setNavigationLock(false);
          }, DeliveryCalendarPicker.SCROLL_SETTLE_MS);
        }
      }
    };

    if (shipDateEl) {
      shipDateEl.addEventListener('click', jumpToSelectedMonth);
      shipDateEl.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          jumpToSelectedMonth();
        }
      });
    }

    // Note: Swipe is now handled natively by CSS scroll-snap on the carousel

    const initializeSelection = () => {
      const sharedState = getSharedShipDateState();
      if (sharedState?.value && !getShipDateReset()?.pending && !hasShipDateRequests() && !shipDateWriter.orderEnded) {
        const sharedEntry = dateMap[sharedState.value];
        if (sharedEntry) {
          applySelection(sharedEntry, { skipCartUpdate: true, broadcast: false });
          return;
        }
      }

      if (defaultSelection) {
        applySelection(defaultSelection, { skipCartUpdate: true, broadcast: false });
        return;
      }

      updateShipDateDisplay(''); // No selection yet
      updateNav();
      renderGrid();
    };

    initializeSelection();
    if (reset?.pending && reset.failed) showShipDateSaveError(true);

    if (reset && (reset.pending || hasShipDateRequests(reset.token))) {
      const generation = this.uniqueId;
      const revision = shipDateWriter.revision;
      if (reset.orphaned || reset.flight) scheduleShipDateRecovery(reset);
      Promise.resolve(reset.cleanup).then(() => readShipDateCart(3)).then(async (result) => {
        if (getShipDateReset() !== reset || (!reset.pending && !hasShipDateRequests(reset.token))) return;
        if (result.outcome !== 'saved' || !result.cart?.token) {
          if (revision === shipDateWriter.revision) {
            showShipDateSaveError(true);
            scheduleShipDateRecovery(reset);
          }
          return;
        }
        // Check token identity before any restored marker can clear a cart.
        if (result.cart.token !== reset.token) {
          useShipDateCart(result.cart);
          if (this.isConnected && this.uniqueId === generation && revision === shipDateWriter.revision) {
            const current = dateMap[result.cart.attributes?.ship_date];
            if (current) applySelection(current, { skipCartUpdate: true, broadcast: false });
          }
          return;
        }
        reset.verified = true;
        if (reset.flight) observeFallbackShipDateRequest(reset);
        if (reset.indeterminate || reset.orphaned || reset.flight || hasShipDateRequests(reset.token)) {
          scheduleShipDateRecovery(reset);
          showShipDateSaveError(true);
          return;
        }
        if (shipDateWriter.running || revision !== shipDateWriter.revision) return;
        if ((result.cart.attributes.ship_date || '') === (shipDateWriter.latest?.value || '')) {
          completeShipDateReset(reset);
          shipDateWriter.confirmedRevision = revision;
          publishConfirmedShipDate(shipDateWriter.latest?.value, revision);
        }
        else await writeShipDate(shipDateWriter.latest?.value || '', reset.token);
      });
    }

    if (typeof window !== 'undefined') {
      if (this.handleSharedSelectionEvent) {
        window.removeEventListener(SHIP_DATE_EVENT_NAME, this.handleSharedSelectionEvent);
      }
      this.handleSharedSelectionEvent = (event) => {
        const detail = event.detail || {};
        if (detail.token && detail.token === getShipDateReset()?.token && !getShipDateReset()?.pending) {
          applySelection(dateMap[detail.value] || firstAvailableDate || deliveryDates[0], { skipCartUpdate: true, broadcast: false });
          return;
        }
        if (detail.status === 'synced' && detail.value === dateInput.value && !getShipDateReset()?.pending) {
          this.classList.remove('delivery-calendar--save-error');
          showError('');
        }
        if (!detail.value || detail.sourceId === this.calendarInstanceId || getShipDateReset()?.pending) {
          return;
        }
        const sharedEntry = dateMap[detail.value];
        if (!sharedEntry) return;
        applySelection(sharedEntry, { skipCartUpdate: true, broadcast: false });
      };
      window.addEventListener(SHIP_DATE_EVENT_NAME, this.handleSharedSelectionEvent);
    }

    const checkoutReady = () => dateInput.value && !getShipDateReset()?.pending && !hasShipDateRequests() && !shipDateWriter.running
      && (!shipDateWriter.latest || shipDateWriter.confirmedRevision === shipDateWriter.revision);
    this.replayCheckout = () => {
      const pending = this.pendingCheckout;
      if (!pending || !checkoutReady()) return;
      this.pendingCheckout = null;
      if (this.isConnected && pending.button?.isConnected && pending.revision === shipDateWriter.revision
        && pending.revision === shipDateWriter.confirmedRevision) {
        if (Date.now() <= pending.until) pending.button.click();
        else showShipDateSaveError(true);
      }
    };
    this.handleFormSubmit = (event) => {
      if (checkoutReady()) {
        return;
      }
      event.preventDefault();
      if (!dateInput.value) showError(requiredMessage);
      else if (shipDateWriter.running && !getShipDateReset()?.failed && !getShipDateReset()?.indeterminate) {
        this.pendingCheckout = {
          button: event.type === 'click' ? event.currentTarget : event.submitter || this.checkoutButtons[0],
          revision: shipDateWriter.revision, until: Date.now() + SHIP_DATE_SAVE_BUDGET_MS,
        };
        return;
      } else showShipDateSaveError(true);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Block checkout buttons if no date selected
    this.checkoutButtons = Array.from(
      this.cartForm?.querySelectorAll('[data-delivery-checkout-button]') || []
    );
    this.handleCheckoutClick = this.handleFormSubmit;

    if (this.cartForm) {
      this.cartForm.addEventListener('submit', this.handleFormSubmit);
    }

    // Add click listeners to checkout buttons
    this.checkoutButtons.forEach((button) => {
      button.addEventListener('click', this.handleCheckoutClick);
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    });

    this.dataset.deliveryCalendarReady = 'true';
    this.dataset.deliveryCalendarState = 'ready';
    this.dispatchEvent(new CustomEvent('delivery-calendar:ready', { bubbles: true }));
  }
}

// Register the Web Component
if (!customElements.get('delivery-calendar-picker')) {
  customElements.define('delivery-calendar-picker', DeliveryCalendarPicker);
}

// Export for CommonJS environments (tests, tooling)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryCalendarPicker;
}
